import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import Header from '../component/Header';
import Footer from '../component/footer';
import SignupButton from '../component/sign-up';
import Seo from '../components/Seo';
import { getPlans, Plan } from '../services/planService';
import { CheckoutPlan, setCheckoutPlan } from '../utils/checkoutSession';

const PLAN_DISPLAY_ORDER = ['Basic', 'Premium', 'Standard'];

const parsePriceValue = (value: string) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatPlanPrice = (plan: Plan) => {
  const priceValue = parsePriceValue(plan.price);
  const roundedPrice = Number.isInteger(priceValue) ? priceValue.toString() : priceValue.toFixed(2);

  return plan.name.toLowerCase() === 'basic' ? `€${roundedPrice}` : `€${roundedPrice}`;
};

const getPlanFeatures = (description: string | null) => {
  return (description || '')
    .split(/\r?\n/)
    .map((feature) => feature.trim())
    .filter(Boolean);
};

const mapApiPlanToCheckoutPlan = (plan: Plan): CheckoutPlan => {
  const priceValue = parsePriceValue(plan.price);

  return {
    id: plan.id,
    name: plan.name,
    price: formatPlanPrice(plan),
    priceValue,
    period: '/Per Month',
    features: getPlanFeatures(plan.description),
    summaryTitle: `Monthly ${plan.name} Subscription`,
    billingDescription: 'Rebills every month',
  };
};

const sortPlansForDisplay = (plans: CheckoutPlan[]) => {
  return [...plans].sort((firstPlan, secondPlan) => {
    const firstIndex = PLAN_DISPLAY_ORDER.indexOf(firstPlan.name);
    const secondIndex = PLAN_DISPLAY_ORDER.indexOf(secondPlan.name);

    if (firstIndex !== -1 || secondIndex !== -1) {
      return (firstIndex === -1 ? Number.MAX_SAFE_INTEGER : firstIndex) -
        (secondIndex === -1 ? Number.MAX_SAFE_INTEGER : secondIndex);
    }

    return firstPlan.name.localeCompare(secondPlan.name);
  });
};

const Pricing = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<CheckoutPlan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      try {
        setIsLoadingPlans(true);
        setPlansError('');

        const response = await getPlans();
        const activePlans = (response.data || [])
          .filter((plan) => plan.is_active)
          .map(mapApiPlanToCheckoutPlan);

        if (isMounted) {
          setPlans(sortPlansForDisplay(activePlans));
        }
      } catch (error) {
        if (isMounted) {
          setPlansError(error instanceof Error ? error.message : 'Unable to load plans.');
        }
      } finally {
        if (isMounted) {
          setIsLoadingPlans(false);
        }
      }
    };

    loadPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleGetStarted = (plan: CheckoutPlan) => {
    setCheckoutPlan(plan);
    navigate('/checkout', { state: { plan } });
  };

  return (
    <>
      <Seo
        title="Pricing Plans for Web Development Courses | LearnWebAcademy"
        description="Explore affordable pricing plans for web development courses with flexible access, expert-led training, and valuable learning resources."
        canonicalPath="/pricing"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              '@id': 'https://learnwebacademy.net/#organization',
              name: 'LearnWebAcademy',
              url: 'https://learnwebacademy.net',
              logo: 'https://learnwebacademy.net/LOGO-web-academy.svg',
            },
            {
              '@type': 'WebPage',
              '@id': 'https://learnwebacademy.net/pricing#webpage',
              url: 'https://learnwebacademy.net/pricing',
              name: 'Pricing Plans for Web Development Courses | LearnWebAcademy',
              description: 'Explore affordable pricing plans for web development courses with flexible access, expert-led training, and valuable learning resources.',
              about: {
                '@id': 'https://learnwebacademy.net/#organization',
              },
            },
            {
              '@type': 'OfferCatalog',
              '@id': 'https://learnwebacademy.net/pricing#offer-catalog',
              name: 'Web Development Course Pricing Plans',
              url: 'https://learnwebacademy.net/pricing',
              itemListElement: plans.map((plan) => ({
                '@type': 'Offer',
                name: `${plan.name} Plan`,
                price: plan.priceValue,
                priceCurrency: 'EUR',
                availability: 'https://schema.org/InStock',
                url: 'https://learnwebacademy.net/pricing',
                itemOffered: {
                  '@type': 'Course',
                  name: `${plan.name} Web Development Course Access`,
                  provider: {
                    '@id': 'https://learnwebacademy.net/#organization',
                  },
                },
              })),
            },
          ],
        }}
      />
      <Header />

      <div className="min-h-screen flex flex-col bg-[#F4F5F7]">
        <main className="flex-grow pt-32 pb-24 px-6 md:px-12">
          <div className="max-w-7xl mx-auto text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-[#113A28] tracking-tight mb-4">
              Simple, transparent pricing
            </h1>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Choose the plan that best fits your learning goals. Upgrade, downgrade, or cancel anytime.
            </p>
          </div>

          {plansError && (
            <p className="max-w-2xl mx-auto mb-8 text-center text-sm font-medium text-red-500">
              {plansError}
            </p>
          )}

          {isLoadingPlans && (
            <p className="max-w-2xl mx-auto mb-8 text-center text-sm font-medium text-gray-500">
              Loading plans...
            </p>
          )}

          {!isLoadingPlans && !plansError && plans.length === 0 && (
            <p className="max-w-2xl mx-auto mb-8 text-center text-sm font-medium text-gray-500">
              No plans are available right now.
            </p>
          )}

          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-8 items-center">
            {!isLoadingPlans && !plansError && plans.map((plan) => {
              const isPopular = plan.name === 'Premium';

              return (
                <div
                  key={plan.name}
                  className={`relative rounded-[2.5rem] overflow-hidden transition-all duration-500 hover:shadow-2xl flex flex-col ${
                    isPopular
                      ? 'bg-[#113A28] text-white shadow-xl md:-translate-y-4 md:scale-105 z-10 border-4 border-[#4CE470]/20'
                      : 'bg-white text-[#113A28] shadow-md border border-gray-100 hover:-translate-y-2'
                  }`}
                >
                  <div
                    className={`py-6 text-center border-b ${
                      isPopular ? 'border-white/10 bg-white/5' : 'border-gray-100 bg-gray-50'
                    }`}
                  >
                    <h3
                      className={`text-2xl font-bold tracking-wide ${
                        isPopular ? 'text-[#4CE470]' : 'text-[#113A28]'
                      }`}
                    >
                      {plan.name}
                    </h3>
                  </div>

                  <div className="p-8 md:p-10 flex flex-col flex-grow">
                    <div className="text-center mb-10">
                      <span className="text-5xl font-bold">{plan.price}</span>
                      <span className={`text-sm ${isPopular ? 'text-gray-300' : 'text-gray-500'}`}>
                        {plan.period}
                      </span>
                    </div>

                    <div className="space-y-4 mt-8">
                      {plan.features.map((feature) => (
                        <div key={feature} className="flex items-center gap-4">
                          <Check
                            size={28}
                            strokeWidth={3}
                            className={`flex-shrink-0 ${isPopular ? 'text-[#4CE470]' : 'text-[#113A28]'}`}
                          />
                          <span
                            className={`text-base font-medium ${isPopular ? 'text-gray-200' : 'text-gray-600'}`}
                          >
                            {feature}
                          </span>
                        </div>
                      ))}
                      <SignupButton
                        text="GET STARTED"
                        className="w-full"
                        onClick={() => handleGetStarted(plan)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </main>
      </div>

      <Footer />
    </>
  );
};

export default Pricing;
